// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DEMO_MESSAGES = [
  { from: 'bot', text: 'Hi! Welcome to our business. How can I help you today?' },
  { from: 'user', text: 'What are your business hours?' },
  { from: 'bot', text: 'We\'re open Monday-Friday 9AM-5PM MST, and Saturday 10AM-2PM. Is there anything else I can help with?' },
  { from: 'user', text: 'Do you offer free consultations?' },
  { from: 'bot', text: 'Yes! We offer a free 30-minute initial consultation. Would you like me to help you book one?' },
];

const DEMO_FAQS = [
  { q: 'What are your business hours?', a: 'Monday-Friday 9AM-5PM MST, Saturday 10AM-2PM.' },
  { q: 'Do you offer free consultations?', a: 'Yes, we offer a free 30-minute initial consultation.' },
  { q: 'Where are you located?', a: 'We\'re located in Chestermere, Alberta, Canada.' },
  { q: 'How do I contact support?', a: 'Email us at support@yourbusiness.com or call (403) 555-0100.' },
  { q: 'What payment methods do you accept?', a: 'We accept Visa, Mastercard, e-Transfer, and invoice payments.' },
];

function ChatbotBuilderPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('preview');
  const [botName, setBotName] = useState('Forge Assistant');
  const [botTone, setBotTone] = useState('professional');
  const [faqs, setFaqs] = useState(DEMO_FAQS);

  const stats = [
    { label: 'Total Conversations', value: '1,284', color: '#6366f1' },
    { label: 'Resolution Rate', value: '78%', color: '#10b981' },
    { label: 'Avg Response Time', value: '1.2s', color: '#3b82f6' },
    { label: 'Handoff Rate', value: '12%', color: '#f59e0b' },
  ];

  const renderPreview = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20 }}>
      <div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Bot Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Bot Name</label><input type="text" value={botName} onChange={e => setBotName(e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>Tone</label>
              <select value={botTone} onChange={e => setBotTone(e.target.value)} style={inputStyle}>
                <option value="professional">Professional</option><option value="friendly">Friendly</option><option value="casual">Casual</option>
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {stats.map(s => (
            <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</p><p style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 500 }}>
        <div style={{ padding: '16px 20px', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{'\uD83E\uDD16'}</div>
          <div><div style={{ fontSize: 14, fontWeight: 600 }}>{botName}</div><div style={{ fontSize: 11, opacity: 0.8 }}>Online</div></div>
        </div>
        <div style={{ flex: 1, padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DEMO_MESSAGES.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.from === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '80%', padding: '10px 14px', borderRadius: m.from === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: m.from === 'user' ? '#6366f1' : 'var(--bg)', color: m.from === 'user' ? '#fff' : 'var(--text)', fontSize: 13, lineHeight: 1.5 }}>{m.text}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
          <input type="text" placeholder="Type a message..." style={{ ...inputStyle, borderRadius: 20 }} />
          <button style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, cursor: 'pointer' }}>Send</button>
        </div>
      </div>
    </div>
  );

  const renderKnowledge = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Knowledge Base</h2>
      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {faqs.map((faq, i) => (
          <div key={i} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Q: {faq.q}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>A: {faq.a}</p>
          </div>
        ))}
      </div>
      <div style={{ background: 'var(--card)', border: '1px dashed var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Add Training Data</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          <div><label style={labelStyle}>Question</label><input type="text" placeholder="What question should the bot answer?" style={inputStyle} /></div>
          <div><label style={labelStyle}>Answer</label><textarea rows={2} placeholder="The answer the bot should give..." style={{ ...inputStyle, resize: 'vertical' }} /></div>
          <button style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', alignSelf: 'start' }}>Add to Knowledge Base</button>
        </div>
      </div>
      <div style={{ marginTop: 20, padding: 16, background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Bulk Training</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ padding: '8px 16px', background: 'var(--card)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Upload FAQ Document</button>
          <button style={{ padding: '8px 16px', background: 'var(--card)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Scan Website Pages</button>
        </div>
      </div>
    </div>
  );

  const renderEmbed = () => (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Embed Widget</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>Add this code to your website to embed the chat widget.</p>
      <code style={{ display: 'block', padding: 16, background: 'var(--bg)', borderRadius: 8, fontSize: 13, color: '#6366f1', lineHeight: 1.8, wordBreak: 'break-all', border: '1px solid var(--border)' }}>
        {'<script src="https://forge.pages.dev/chatbot.js"'}<br />
        {'  data-bot-name="' + botName + '"'}<br />
        {'  data-tone="' + botTone + '"'}<br />
        {'  data-color="#6366f1"'}<br />
        {'  data-position="bottom-right">'}<br />
        {'</script>'}
      </code>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Copy Code</button>
        <button style={{ padding: '8px 16px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>Test Widget</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83E\uDD16'} AI Chatbot</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>Build an AI-powered support chatbot for your website.</p>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['preview', 'knowledge', 'embed'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t === 'knowledge' ? 'Knowledge Base' : t}</button>
        ))}
      </div>
      {tab === 'preview' && renderPreview()}
      {tab === 'knowledge' && renderKnowledge()}
      {tab === 'embed' && renderEmbed()}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default ChatbotBuilderPage;
